import { useCallback, useRef, useState } from "react";
import {
  IconArrowsMaximize,
  IconPictureInPicture,
  IconX,
} from "@tabler/icons-react";
import ModalDialog from "../UI/ModalDialog";
import Callout from "../UI/Callout";
import Button from "../UI/Button";
import IconButton from "../UI/IconButton";
import Title from "../UI/Title";
import Text from "../UI/Text";
import TutorialVideoList from "./TutorialVideoList";
import useVimeoPlayer, { vimeoEmbedUrl } from "./useVimeoPlayer";
import {
  TUTORIAL_PAGE_LABELS,
  type TutorialPage,
  type TutorialVideo,
} from "./videoTutorials";
import styles from "./VideoTutorialPlayer.module.css";

export type PlayerView = "modal" | "mini";

interface VideoTutorialPlayerProps {
  page: TutorialPage;
  videos: TutorialVideo[];
  current: TutorialVideo;
  view: PlayerView;
  seenIds: string[];
  onViewChange: (view: PlayerView) => void;
  onSelect: (video: TutorialVideo) => void;
  onClose: () => void;
}

interface VimeoFrameProps {
  video: TutorialVideo;
  startAt: number;
  onTimeUpdate: (seconds: number) => void;
  className?: string;
}

function VimeoFrame({
  video,
  startAt,
  onTimeUpdate,
  className,
}: VimeoFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useVimeoPlayer(iframeRef, onTimeUpdate);
  return (
    <iframe
      ref={iframeRef}
      className={className}
      src={vimeoEmbedUrl(video.id, startAt)}
      title={video.title}
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
    />
  );
}

/**
 * Lecteur des tutos vidéo : en modale (vidéo + autres tutos de la page) ou
 * en mini-lecteur flottant pour suivre les étapes dans Qalimo en même temps.
 * Le changement de vue remonte l'iframe : la vidéo reprend à la position
 * mémorisée via l'API du lecteur.
 */
export default function VideoTutorialPlayer({
  page,
  videos,
  current,
  view,
  seenIds,
  onViewChange,
  onSelect,
  onClose,
}: VideoTutorialPlayerProps) {
  const positionRef = useRef(0);
  const [startAt, setStartAt] = useState(0);

  const handleTimeUpdate = useCallback((seconds: number) => {
    positionRef.current = seconds;
  }, []);

  function switchView(next: PlayerView) {
    setStartAt(positionRef.current);
    onViewChange(next);
  }

  function selectVideo(video: TutorialVideo) {
    positionRef.current = 0;
    setStartAt(0);
    onSelect(video);
  }

  if (view === "mini") {
    return (
      <div
        className={styles.mini}
        role="region"
        aria-label="Mini-lecteur vidéo"
      >
        <VimeoFrame
          key={current.id}
          video={current}
          startAt={startAt}
          onTimeUpdate={handleTimeUpdate}
          className={styles.miniFrame}
        />
        <div className={styles.miniBar}>
          <Text variant="deci" weight="bold" className={styles.miniTitle}>
            {current.title}
          </Text>
          <IconButton
            icon={IconArrowsMaximize}
            label="Agrandir"
            variant="plain"
            size="sm"
            onClick={() => switchView("modal")}
          />
          <IconButton
            icon={IconX}
            label="Fermer le mini-lecteur"
            variant="plain"
            size="sm"
            onClick={onClose}
          />
        </div>
      </div>
    );
  }

  const others = videos.filter((video) => video.id !== current.id);

  return (
    <ModalDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="xl"
      header={current.title}
      subtitle={`Tutoriel · ${TUTORIAL_PAGE_LABELS[page]}`}
    >
      <div className={styles.modalBody}>
        <div className={styles.frameWrapper}>
          <VimeoFrame
            key={current.id}
            video={current}
            startAt={startAt}
            onTimeUpdate={handleTimeUpdate}
            className={styles.frame}
          />
        </div>
        <aside className={styles.sidebar}>
          {others.length > 0 && (
            <div className={styles.others}>
              <Title as="div" variant="deca" weight="bold">
                Autres tutos de cette page
              </Title>
              <TutorialVideoList
                videos={others}
                seenIds={seenIds}
                onSelect={selectVideo}
              />
            </div>
          )}
          <Callout
            variant="info"
            icon={IconPictureInPicture}
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => switchView("mini")}
              >
                Réduire
              </Button>
            }
          >
            Réduisez la vidéo pour suivre les étapes en même temps dans Qalimo.
          </Callout>
        </aside>
      </div>
    </ModalDialog>
  );
}
